import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import backgroundImage from '../assets/abc.gif'; // Adjust path based on your project structure

// Inject keyframes for custom space animations
const injectKeyframes = () => {
  const style = document.createElement('style');
  style.innerHTML = `
    @keyframes orbit {
      0% { transform: rotate(0deg) translateX(15px) rotate(0deg); }
      100% { transform: rotate(360deg) translateX(15px) rotate(-360deg); }
    }
    @keyframes twinkle {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.3; transform: scale(0.8); }
    }
    @keyframes float {
      0%, 100% { transform: translateY(0) rotate(0deg); }
      50% { transform: translateY(-20px) rotate(10deg); }
    }
    @keyframes glowPulse {
      0%, 100% { box-shadow: 0 0 5px #a855f7, 0 0 10px #3b82f6; }
      50% { box-shadow: 0 0 10px #a855f7, 0 0 20px #3b82f6; }
    }
    @keyframes lineFlow {
      0% { background-position: 0% 0%; }
      100% { background-position: 0% 100%; }
    }
    @ keyframes dotPulse {
      0%, 100% { transform: translateX(-50%) scale(1); }
      50% { transform: translateX(-50%) scale(1.2); }
    }
  `;
  document.head.appendChild(style);
};

const Experience = () => {
  useEffect(() => {
    injectKeyframes();
  }, []);

  const experiences = [
    {
      role: 'Python Developer Intern',
      company: 'Metavy',
      date: 'June 2023 - August 2023',
      website: 'https://www.metavy.com',
      description: [
        'Developed machine learning models using Python and Scikit-learn.',
        'Implemented data preprocessing techniques for improved accuracy.',
        'Collaborated with a team of data scientists on production deployments.',
        'Gained experience in data analysis, machine learning, and Python.',
      ],
    },
    {
      role: 'Volunteer',
      company: 'Sudhama Old Age Home',
      date: 'June 2024',
      website: 'https://www.sudhamaoldagehome.org',
      description: [
        'Assisted elderly residents and conducted educational workshops.',
        'Developed communication, empathy, and organizational skills.',
      ],
    },
    {
      role: 'React Developer (Remote, Full-Time)',
      company: 'kaanch Inc',
      date: 'August 2024 - February 2025',
      website: 'https://kaanch.com/',
      description: [
        'Developed responsive front-end components using React.js, enhancing overall user experience.',
        'Integrated RESTful APIs with backend teams to ensure smooth data flow.',
        'Built reusable UI components and optimized performance across multiple screens.',
      ],
    },
    {
      role: 'Fullstack Web Development Intern',
      company: 'Unified Mentor Pvt Ltd',
      date: 'December 2024 - January 2025',
      website: 'https://www.unifiedmentor.com',
      description: [
        'Architected vibrant, fullstack web solutions, seamlessly bridging frontend and backend.',
        'Thrived in a high-energy environment, mastering cutting-edge technologies.',
        'Amplified proficiency in modern web frameworks.',
      ],
    },
  ];

  // CSS styles
  const sectionStyle = {
    padding: '5rem 1.5rem',
    minHeight: '100vh',
    color: '#e5e7eb',
    backgroundColor: '#000000',
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    position: 'relative',
    overflow: 'hidden',
  };

  const titleStyle = {
    fontSize: '3rem',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '4rem',
    color: '#c084fc',
  };

  const timelineContainerStyle = {
    position: 'relative',
    maxWidth: '800px',
    margin: '0 auto',
  };

  const timelineLineStyle = {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '4px',
    height: '100%',
    background: 'linear-gradient(to bottom, #a855f7, #3b82f6)',
    backgroundSize: '100% 200%',
    animation: 'lineFlow 4s linear infinite',
  };

  const experienceItemStyle = (index) => ({
    position: 'relative',
    marginBottom: '3rem',
    display: 'flex',
    justifyContent: index % 2 === 0 ? 'flex-end' : 'flex-start',
  });

  const cardStyle = (index) => ({
    width: '45%',
    backgroundColor: '#1f2937',
    padding: '1.5rem',
    borderRadius: '1rem',
    border: '1px solid #a855f7',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
    margin: index % 2 === 0 ? '0 0 0 2rem' : '0 2rem 0 0',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  });

  const roleStyle = {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#d8b4fe',
    marginBottom: '0.5rem',
  };

  const linkStyle = {
    color: '#60a5fa',
    textDecoration: 'none',
  };

  const dateStyle = {
    fontSize: '0.875rem',
    color: '#9ca3af',
    marginBottom: '1rem',
  };

  const listStyle = {
    listStyleType: 'disc',
    paddingLeft: '1.25rem',
    color: '#d1d5db',
  };

  const listItemStyle = {
    marginBottom: '0.5rem',
  };

  const timelineDotStyle = {
    position: 'absolute',
    left: '50%',
    top: '1rem',
    width: '1rem',
    height: '1rem',
    backgroundColor: '#a855f7',
    borderRadius: '50%',
    border: '2px solid #3b82f6',
    transform: 'translateX(-50%)',
    animation: 'dotPulse 2s ease-in-out infinite',
  };

  const starStyle = (i) => ({
    position: 'absolute',
    backgroundColor: '#ffffff',
    borderRadius: '50%',
    width: `${Math.random() * 3 + 1}px`,
    height: `${Math.random() * 3 + 1}px`,
    top: `${Math.random() * 100}%`,
    left: `${Math.random() * 100}%`,
  });

  return (
    <section id="experience" style={sectionStyle}>
      {/* Background stars */}
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          style={starStyle(i)}
          animate={{ opacity: [1, 0.3, 1], scale: [1, 0.8, 1] }}
          transition={{ duration: Math.random() * 2 + 1, repeat: Infinity }}
        />
      ))}

      <motion.h2
        style={titleStyle}
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        Work Experience.
      </motion.h2>

      <div style={timelineContainerStyle}>
        {/* Central timeline line */}
        <motion.div
          style={timelineLineStyle}
          initial={{ height: 0 }}
          animate={{ height: '100%' }}
          transition={{ duration: 1.5, ease: 'easeInOut' }}
        />

        <AnimatePresence>
          {experiences.map((exp, index) => (
            <motion.div
              key={index}
              style={experienceItemStyle(index)}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: index * 0.3 }}
            >
              <motion.div
                style={cardStyle(index)}
                whileHover={{
                  scale: 1.05,
                  boxShadow: '0 0 15px rgba(168, 85, 247, 0.5)',
                }}
              >
                <h3 style={roleStyle}>
                  {exp.role} at{' '}
                  <a
                    href={exp.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={linkStyle}
                  >
                    {exp.company}
                  </a>
                </h3>
                <p style={dateStyle}>{exp.date}</p>
                <ul style={listStyle}>
                  {exp.description.map((item, i) => (
                    <motion.li
                      key={i}
                      style={listItemStyle}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: 0.2 * i + 0.5,
                        duration: 0.5,
                        type: 'spring',
                        stiffness: 100,
                      }}
                    >
                      {item}
                      </motion.li>
                  ))}
                </ul>
              </motion.div>

              {/* Connecting dot on timeline */}
              <motion.div
                style={timelineDotStyle}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.3 }}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Experience;